import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useToast } from '../context/ToastContext';
import { formatPrice, formatImageUrl } from '../utils/helpers';
import { Trash2, Plus, Minus, ShoppingBag, CreditCard, Truck } from 'lucide-react';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Cart = () => {
  const { cart, cartTotal, removeFromCart, removeServiceFromCart, updateCartItem, loading } = useCart();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const [paymentMethods, setPaymentMethods] = useState([]);

  // Check if cart contains only services
  const isServiceOnly = (!cart.products || cart.products.length === 0) && cart.services && cart.services.length > 0;

  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  const fetchPaymentMethods = async () => {
    try {
      const response = await axios.get(`${API_URL}/payment-methods?active_only=true`);
      setPaymentMethods(response.data);
    } catch (error) {
      console.error('Failed to fetch payment methods:', error);
    }
  };

  const handleQuantityChange = async (productId, currentQuantity, change) => {
    const newQuantity = currentQuantity + change;
    if (newQuantity < 1) return;
    
    await updateCartItem(productId, newQuantity);
  };

  const handleRemove = async (productId, productName) => {
    await removeFromCart(productId);
    addToast(`${productName} eltávolítva a kosárból`, 'info');
  };

  const handleRemoveService = async (index, serviceName) => {
    await removeServiceFromCart(index);
    addToast(`${serviceName} eltávolítva a kosárból`, 'info');
  };

  if (loading) {
    return (
      <div className="relative min-h-screen pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-300">Betöltés...</p>
        </div>
      </div>
    );
  }

  const isEmpty = (!cart.products || cart.products.length === 0) && (!cart.services || cart.services.length === 0);

  if (isEmpty) {
    return (
      <div className="relative min-h-screen pt-24 pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="glass rounded-2xl p-12 text-center">
            <ShoppingBag className="w-20 h-20 mx-auto mb-6 text-gray-500" />
            <h2 className="text-3xl font-semibold mb-4">A kosarad üres</h2>
            <p className="text-gray-400 mb-8">Még nem adtál hozzá termékeket vagy szolgáltatásokat a kosaradhoz.</p>
            <div className="flex gap-4 justify-center">
              <Link to="/termekek" className="btn-magic rounded-lg px-8 py-3 inline-block">
                Termékek böngészése
              </Link>
              <Link to="/szolgaltatasok" className="btn-magic rounded-lg px-8 py-3 inline-block">
                Szolgáltatások böngészése
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="section-title text-4xl md:text-5xl mb-8 text-center">
          <span>Kosár</span>
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-6">
            {/* Products Section */}
            {cart.products && cart.products.length > 0 && (
              <div className="space-y-4">
                <h2 className="text-2xl font-semibold text-white flex items-center gap-2">
                  <ShoppingBag className="w-6 h-6" />
                  Termékek
                </h2>
                {cart.products.map((item) => (
                  <div key={item.product_id} className="glass rounded-xl p-6">
                    <div className="flex gap-4">
                      {/* Product Image */}
                      <div className="w-24 h-24 flex-shrink-0">
                        {item.product_image ? (
                          <img
                            src={formatImageUrl(item.product_image)}
                            alt={item.product_name}
                            className="w-full h-full object-cover rounded-lg"
                          />
                        ) : (
                          <div className="w-full h-full bg-black/30 rounded-lg flex items-center justify-center">
                            <span className="text-3xl">✨</span>
                          </div>
                        )}
                      </div>

                      {/* Product Info */}
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-white mb-2">{item.product_name}</h3>
                        <p className="text-yellow-200 font-semibold">{formatPrice(item.product_price)}</p>
                      </div>

                      {/* Quantity Controls */}
                      <div className="flex flex-col items-end gap-4">
                        <button
                          onClick={() => handleRemove(item.product_id, item.product_name)}
                          className="text-red-400 hover:text-red-300 transition"
                          title="Eltávolítás"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>

                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleQuantityChange(item.product_id, item.quantity, -1)}
                            className="bg-white/10 hover:bg-white/20 p-2 rounded-lg transition"
                            disabled={item.quantity <= 1}
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                          <span className="w-12 text-center font-semibold">{item.quantity}</span>
                          <button
                            onClick={() => handleQuantityChange(item.product_id, item.quantity, 1)}
                            className="bg-white/10 hover:bg-white/20 p-2 rounded-lg transition"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>

                        <div className="text-right">
                          <p className="text-sm text-gray-400">Összesen</p>
                          <p className="text-lg font-bold text-yellow-200">{formatPrice(item.subtotal)}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Services Section */}
            {cart.services && cart.services.length > 0 && (
              <div className="space-y-4">
                <h2 className="text-2xl font-semibold text-white flex items-center gap-2">
                  ✨ Szolgáltatások
                </h2>
                {cart.services.map((item, index) => (
                  <div key={`service-${index}`} className="glass rounded-xl p-6 border-yellow-200/20">
                    <div className="flex gap-4">
                      {/* Service Image */}
                      <div className="w-24 h-24 flex-shrink-0">
                        {item.service_image ? (
                          <img
                            src={formatImageUrl(item.service_image)}
                            alt={item.service_name}
                            className="w-full h-full object-cover rounded-lg"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-purple-900/30 to-black rounded-lg flex items-center justify-center">
                            <span className="text-3xl">✨</span>
                          </div>
                        )}
                      </div>

                      {/* Service Info */}
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-white mb-2">{item.service_name}</h3>
                        <p className="text-yellow-200 font-semibold mb-3">{formatPrice(item.service_price)}</p>
                        
                        {/* Service Details */}
                        <div className="text-sm text-gray-400 space-y-1">
                          <p><strong>Név:</strong> {item.full_name}</p>
                          <p><strong>Születési dátum:</strong> {item.birth_date}</p>
                          {item.questions && (
                            <p className="line-clamp-2"><strong>Üzenet:</strong> {item.questions}</p>
                          )}
                        </div>
                      </div>

                      {/* Remove Button */}
                      <div className="flex flex-col items-end gap-4">
                        <button
                          onClick={() => handleRemoveService(index, item.service_name)}
                          className="text-red-400 hover:text-red-300 transition"
                          title="Eltávolítás"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>

                        <div className="text-right mt-auto">
                          <p className="text-sm text-gray-400">Összesen</p>
                          <p className="text-lg font-bold text-yellow-200">{formatPrice(item.subtotal)}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="glass rounded-xl p-6 sticky top-24">
              <h2 className="text-2xl font-semibold mb-6">Összesítő</h2>
              
              <div className="space-y-4 mb-6">
                <div className="flex justify-between">
                  <span className="text-gray-400">Részösszeg</span>
                  <span className="font-semibold">{formatPrice(cartTotal)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Szállítás</span>
                  <span className="font-semibold">
                    {isServiceOnly ? (
                      <span className="text-green-400">Ingyenes</span>
                    ) : (
                      <span className="text-gray-400">A pénztárban</span>
                    )}
                  </span>
                </div>
                {isServiceOnly && (
                  <p className="text-xs text-blue-300 bg-blue-500/10 p-2 rounded">
                    ✨ Online szolgáltatás - nincs szállítási költség
                  </p>
                )}
              </div>

              <div className="border-t border-white/10 pt-4 mb-6">
                <div className="flex justify-between text-xl font-bold">
                  <span>Végösszeg</span>
                  <span className="text-yellow-200">{formatPrice(cartTotal)}</span>
                </div>
                {!isServiceOnly && (
                  <p className="text-xs text-gray-500 mt-2">+ szállítási költség a pénztárban</p>
                )}
              </div>

              {/* Payment Methods Preview */}
              {paymentMethods.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm text-gray-400 mb-3 flex items-center gap-2">
                    <CreditCard className="w-4 h-4" />
                    Elérhető fizetési módok
                  </h3>
                  <div className="space-y-2">
                    {paymentMethods.map(method => (
                      <div key={method.id} className="flex items-center gap-2 text-sm">
                        <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                        <span className="text-gray-300">{method.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <button
                onClick={() => navigate('/penztar')}
                className="w-full btn-magic rounded-lg py-3 text-lg font-semibold mb-4"
              >
                Tovább a fizetéshez
              </button>

              <Link
                to="/termekek"
                className="block w-full text-center glass rounded-lg py-3 hover:bg-white/5 transition"
              >
                Vásárlás folytatása
              </Link>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .glass {
          background: linear-gradient(180deg, hsl(0 0% 100% / 0.04), hsl(0 0% 100% / 0.02));
          border: 1px solid hsl(0 0% 100% / 0.06);
          backdrop-filter: blur(6px);
        }
        .section-title {
          font-family: "Great Vibes", cursive;
          text-shadow: 0 0 20px rgba(255, 255, 255, 0.3);
        }
        .section-title span {
          color: #ffffff;
        }
        .btn-magic {
          position: relative;
          isolation: isolate;
          overflow: hidden;
          border: 1px solid hsl(0 0% 100% / 0.14);
        }
        .btn-magic:before {
          content: '';
          position: absolute;
          inset: -200%;
          background: conic-gradient(from 0deg, transparent, hsl(45 86% 62% / 0.25), transparent 30%);
          animation: spin 6s linear infinite;
          z-index: -1;
          filter: blur(12px);
        }
        @keyframes spin {
          to { transform: rotate(1turn); }
        }
      `}</style>
    </div>
  );
};

export default Cart;